import { useCallback, useContext, useState } from "react";
import { useEditCandidateProfileMutation } from "../../../../App/Queries/CandidateQueries";
import { validateEmail } from "../../../../App/Scripts/emailValidator";
import { IEditCandidateNewEmailAddress } from "../../../../App/Typings/Candidate";
import { CandidateContext } from "../../../CandidateProfile";
import { useNotification } from "../../../../App/State/Hooks/notificationHooks";
import { CandidateEmailType } from "../../../../App/Enums/CandidateEnums";
import { useDebouncedHandler } from "../../../../App/State/Hooks/useDebouncedHandler";

export const useAddEmailState = () => {
  const { candidate } = useContext(CandidateContext);
  const { displaySuccess, displayError, displayInfo } = useNotification();
  const { mutate: editProfile, isLoading } = useEditCandidateProfileMutation();
  const [isAddEmail, setIsAddEmail] = useState(false);
  const [newEmail, setNewEmail] = useState<IEditCandidateNewEmailAddress>({
    emailAddress: "",
    emailType: CandidateEmailType[0],
  });

  const validEmailAddress = validateNewEmailAddress(newEmail);
  const emptyEmailAddress = newEmail.emailAddress === "";
  const disableSave = isLoading || !validEmailAddress || emptyEmailAddress;
  let saveButtonMessage = "Add email address";
  if (isLoading) {
    saveButtonMessage = "Saving...";
  } else if (!validEmailAddress) {
    saveButtonMessage = "Invalid email address";
  } else if (emptyEmailAddress) {
    saveButtonMessage = "A new email address must be entered";
  }

  function handleChangeEmailAddress(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setNewEmail((edit) => ({ ...edit, emailAddress: e.target.value }));
  }
  function handleChangeEmailType(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setNewEmail((edit) => ({
      ...edit,
      emailType: CandidateEmailType[Number(e.target.value)],
    }));
  }

  const submitAddEmail = useCallback(() => {
    if (disableSave) {
      handleCancelChanges();
      displayInfo("Email was not added");
      return;
    }
    editProfile(
      {
        candidateId: candidate.id,
        emailAddress: null,
        phoneNumber: null,
        tags: null,
        newEmailAddress: newEmail,
      },
      {
        onError: () => {
          displayError("Error adding email address");
        },
        onSettled: (response) => {
          if (response?.success === true) {
            setIsAddEmail(false);
            displaySuccess("Successfully added email address");
            setNewEmail({
              emailAddress: "",
              emailType: CandidateEmailType[0],
            });
          } else if (response?.errorMessage) {
            displayError(response?.errorMessage);
          }
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newEmail.emailAddress, newEmail.emailType]);

  const handleCancelChanges = () => {
    setNewEmail({
      emailAddress: "",
      emailType: CandidateEmailType[0],
    });
    setIsAddEmail(false);
  };

  const handleSubmit = useDebouncedHandler(() => {
    submitAddEmail();
  }, 300);

  return {
    getters: {
      isAddEmail,
      newEmail,
      disableSave,
      saveButtonMessage,
      validEmailAddress,
    },
    handlers: {
      setIsAddEmail,
      setNewEmail,
      handleChangeEmailAddress,
      handleChangeEmailType,
      handleCancelChanges,
      handleSubmit,
    },
  } as const;
};

function validateNewEmailAddress(newEmail: IEditCandidateNewEmailAddress): boolean {
  if (newEmail.emailAddress !== "") {
    return Boolean(validateEmail(newEmail.emailAddress));
  }
  return true;
}
